import { useState, useCallback, useRef, useEffect } from 'react'
import {
  Card,
  CardBody,
  CardHeader,
  CardText,
  Badge,
  Button,
  Input,
  Label,
  Row,
  Col,
  Alert,
  Spinner,
} from 'reactstrap'
import Barcode from 'react-barcode'
import QRCode from 'react-qr-code'
import { useNavigate } from 'react-router-dom'
import { useGlobalScanner } from 'hooks/useGlobalScanner'
import { parseQrType } from 'utils/parseQrType'
import './style.scss'

const SETUP_STEPS = [
  {
    step: 1,
    title: 'Aktifkan Mode Bluetooth HID',
    description:
      'Nyalakan scanner, lalu pindai barcode "Bluetooth HID Mode" pada manual. Lampu hijau & biru akan berkedip.',
  },
  {
    step: 2,
    title: 'Masuk Mode Pairing',
    description:
      'Pindai barcode "Pair Setting" pada manual. Scanner siap untuk dipasangkan.',
  },
  {
    step: 3,
    title: 'Hubungkan via Bluetooth',
    description:
      'Buka pengaturan Bluetooth di perangkat Anda → aktifkan Bluetooth → cari dan pilih "Barcode Scanner HID".',
  },
  {
    step: 4,
    title: 'Konfirmasi Koneksi',
    description:
      'Tunggu bunyi "beep" dan lampu biru menyala tetap. Proses pairing membutuhkan sekitar 1 menit.',
  },
  {
    step: 5,
    title: 'Atur Terminal Setting',
    description:
      'Pastikan Terminal Setting scanner diatur ke "Enter" (default). Pindai barcode "Enter" pada manual jika perlu.',
  },
]

const MANUAL_BARCODES = [
  {
    category: '⚙️ Dasar & Reset',
    items: [
      { label: 'Factory Default', value: '$$$X', desc: 'Kembali ke pengaturan awal pabrik jika scanner bermasalah' },
    ],
  },
  {
    category: '🌐 Mode Komunikasi',
    items: [
      { label: 'Bluetooth HID Mode', value: '$$$H', desc: 'Mode keyboard standar (Paling umum digunakan)' },
      { label: '2.4G Receiver Mode', value: '$$$W', desc: 'Gunakan dongle USB 2.4G (Jika tidak pakai Bluetooth)' },
    ],
  },
  {
    category: '🤝 Pairing Setting',
    items: [
      { label: 'Pair Setting', value: '$$$P', desc: 'Pindai ini SETELAH memilih Mode di atas untuk mulai menyambungkan' },
    ],
  },
]

// Helper to decode JWT natively without external libraries
const decodeJWT = (token) => {
  try {
    const parts = token.split('.')
    if (parts.length !== 3) return null
    
    // JWT payload is the second part
    const base64Url = parts[1]
    const base64 = base64Url.replace(/-/g, '+').replace(/_/g, '/')
    
    const jsonPayload = decodeURIComponent(
      window.atob(base64).split('').map(function(c) {
        return '%' + ('00' + c.charCodeAt(0).toString(16)).slice(-2)
      }).join('')
    )
    
    const parsed = JSON.parse(jsonPayload)
    return Object.keys(parsed).length > 0 ? parsed : null
  } catch (e) {
    return null
  }
}

const ScannerSetting = () => {
  const [testScans, setTestScans] = useState([])
  const [isListening, setIsListening] = useState(true)
  const [maxDelay, setMaxDelay] = useState(() => {
    const saved = Number(localStorage.getItem('PMB_scannerMaxDelay'))
    return saved && saved >= 50 ? saved : 500
  })
  const [minLength, setMinLength] = useState(
    () => Number(localStorage.getItem('PMB_scannerMinLength')) || 3,
  )
  const [barcodeInputValue, setBarcodeInputValue] = useState('1234567890')
  const [generatedBarcode, setGeneratedBarcode] = useState('1234567890')
  const [rawKeys, setRawKeys] = useState([])
  const [manualInput, setManualInput] = useState('')
  const [showManual, setShowManual] = useState(false)
  const [showBarcodeValues, setShowBarcodeValues] = useState(false)
  const testAreaRef = useRef(null)
  const navigate = useNavigate()

  // Enable scanner debug logging while on this page
  useEffect(() => {
    localStorage.setItem('PMB_scannerDebug', '1')
    return () => localStorage.removeItem('PMB_scannerDebug')
  }, [])

  // Listen to raw keys to help debug
  useEffect(() => {
    const handleRaw = (e) => {
      if (e.key.length === 1 || e.key === 'Enter') {
        setRawKeys((prev) => {
          const next = [...prev, e.key]
          return next.length > 25 ? next.slice(-25) : next
        })
      }
    }
    window.addEventListener('keydown', handleRaw)
    return () => window.removeEventListener('keydown', handleRaw)
  }, [])

  const handleTestScan = useCallback((scannedValue) => {
    // Add to test history
    setTestScans((prev) => [
      {
        value: scannedValue,
        timestamp: new Date().toLocaleTimeString('id-ID'),
        id: Date.now(),
      },
      ...prev.slice(0, 19), // keep last 20
    ])

    // If it's a command, execute it so we can test navigation directly from here!
    const scanResult = parseQrType(scannedValue)
    if (scanResult.type === 'command') {
      if (scanResult.command === 'createSellTransaction') {
        const prefillOrders = {
          item: scanResult.items.map((it, idx) => {
            const inventoryId = it.gudang || it['gudang?'] || it.inventoryId || null
            return {
              orderItemId: idx + 1,
              itemId: it.itemId || it.id || it.serial || it.ser,
              nameItem: it.nameItem || it.productDescription || it.pd || it.P,
              amount: it.amount || it.qty || '',
              ...(inventoryId ? { inventoryId, originalAmount: true } : {})
            }
          })
        }
        const prefillAddItemCount = scanResult.items.map((_, idx) => idx + 1)
        navigate('/sell-commodity', {
          state: { prefillOrders, prefillAddItemCount, prefillSource: 'scanner' }
        })
      } else if (scanResult.command === 'createInventory') {
        navigate('/inventory', {
          state: { action: 'createInventory', qrItems: scanResult.items }
        })
      }
      return
    }

    // Scroll test area into view
    if (testAreaRef.current) {
      testAreaRef.current.scrollIntoView({
        behavior: 'smooth',
        block: 'center',
      })
    }
  }, [navigate])

  const { isScanning } = useGlobalScanner(handleTestScan, {
    maxDelay,
    minLength,
    enabled: isListening,
  })

  const handleClearScans = () => setTestScans([])

  const handleSaveConfig = () => {
    localStorage.setItem('PMB_scannerMaxDelay', String(maxDelay))
    localStorage.setItem('PMB_scannerMinLength', String(minLength))
  }

  const handleResetConfig = () => {
    setMaxDelay(500)
    setMinLength(3)
    localStorage.removeItem('PMB_scannerMaxDelay')
    localStorage.removeItem('PMB_scannerMinLength')
  }

  return (
    <CardBody className="scanner-setting">
      {/* ─── Scanner Info ─── */}
      <div className="scanner-setting__info-banner">
        <div className="scanner-setting__info-banner-icon">📷</div>
        <div>
          <h6 className="mb-1 fw-semibold">PANDA PRJ-BT444</h6>
          <small className="text-muted">
            2D Barcode Scanner · Bluetooth HID · USB
          </small>
        </div>
        <Badge
          color={isListening ? 'success' : 'secondary'}
          pill
          className="ms-auto scanner-setting__status-badge"
        >
          <span
            className={`scanner-setting__status-dot ${isListening ? 'scanner-setting__status-dot--active' : ''}`}
          />
          {isListening ? 'Mendengarkan' : 'Nonaktif'}
        </Badge>
      </div>

      {/* ─── Setup Guide ─── */}
      <Card className="border-0 shadow-sm mb-4">
        <CardHeader className="bg-transparent border-bottom-0 pb-0 d-flex align-items-center justify-content-between">
          <div>
            <CardText tag="h5" className="fw-semibold mb-0">
              📋 Panduan Pemasangan
            </CardText>
            <small className="text-muted">
              Ikuti langkah-langkah berikut untuk menghubungkan scanner
            </small>
          </div>
          <Button
            color="primary"
            size="sm"
            className="rounded-pill px-3"
            data-testid="setting-scanner-manual-toggle-button"
            onClick={() => setShowManual(!showManual)}
          >
            {showManual ? '🙈 Sembunyikan Manual' : '📖 Lihat Manual Digital'}
          </Button>
        </CardHeader>
        <CardBody>
          <div className="scanner-setting__steps">
            {SETUP_STEPS.map((item) => (
              <div key={item.step} className="scanner-setting__step">
                <div className="scanner-setting__step-number">{item.step}</div>
                <div className="scanner-setting__step-content">
                  <h6 className="fw-semibold mb-1">{item.title}</h6>
                  <p className="text-muted mb-0 small">{item.description}</p>
                </div>
              </div>
            ))}
          </div>

          <Alert color="info" className="mt-3 mb-0 small">
            <strong>💡 Tips:</strong> Pastikan tidak ada input field yang aktif
            (terfokus) saat memindai, karena scanner mengirim data seperti
            ketikan keyboard.
          </Alert>
        </CardBody>
      </Card>

      {/* ─── Digital Manual Section ─── */}
      {showManual && (
        <Card className="border-0 shadow-sm mb-4 scanner-setting__manual-card animate__animated animate__fadeIn">
          <CardHeader className="bg-primary text-white d-flex align-items-center justify-content-between border-0">
            <div className="d-flex align-items-center gap-2">
              <span style={{ fontSize: '1.2rem' }}>📖</span>
              <div>
                <h5 className="mb-0 fw-bold">Manual Konfigurasi Digital</h5>
                <small className="opacity-75">Pindai barcode di bawah ini langsung dari layar</small>
              </div>
            </div>
            <div className="d-flex align-items-center gap-2">
              <div className="form-check form-switch mb-0">
                <Input 
                  type="switch" 
                  id="showValues" 
                  checked={showBarcodeValues} 
                  onChange={() => setShowBarcodeValues(!showBarcodeValues)}
                  className="cursor-pointer"
                />
                <Label for="showValues" className="mb-0 text-white small cursor-pointer ms-1">
                  Lihat Nilai
                </Label>
              </div>
            </div>
          </CardHeader>
          <CardBody className="bg-light">
            <div className="scanner-setting__manual-grid">
              {MANUAL_BARCODES.map((cat, idx) => (
                <div key={idx} className="scanner-setting__manual-category mb-4">
                  <h6 className="fw-bold text-primary border-bottom pb-2 mb-3 d-flex align-items-center gap-2">
                    {cat.category}
                  </h6>
                  <Row className="g-3">
                    {cat.items.map((item, iIdx) => (
                      <Col key={iIdx} xs={12} sm={6} lg={4}>
                        <Card className="h-100 border-0 shadow-sm text-center p-3 scanner-setting__barcode-item">
                          <div className="mb-2">
                            <h6 className="fw-semibold mb-1 small">{item.label}</h6>
                            <p className="text-muted mb-2" style={{ fontSize: '11px' }}>{item.desc}</p>
                          </div>
                          <div className="bg-white p-2 rounded mb-2 d-flex justify-content-center align-items-center">
                            <Barcode 
                              value={item.value} 
                              height={50} 
                              width={1.5} 
                              fontSize={10}
                              displayValue={showBarcodeValues}
                              margin={0}
                            />
                          </div>
                          {showBarcodeValues && (
                            <code className="bg-light px-2 py-1 rounded" style={{ fontSize: '10px' }}>
                              {item.value}
                            </code>
                          )}
                        </Card>
                      </Col>
                    ))}
                  </Row>
                </div>
              ))}
            </div>
            <div className="text-center mt-3">
              <Alert color="warning" className="small mb-0">
                <strong>Catatan:</strong> Jika barcode sulit dibaca dari layar, naikkan kecerahan (brightness) layar atau perbesar halaman (Zoom In).
              </Alert>
            </div>
          </CardBody>
        </Card>
      )}

      {/* ─── LED Indicator Guide ─── */}
      <Card className="border-0 shadow-sm mb-4">
        <CardHeader className="bg-transparent border-bottom-0 pb-0">
          <CardText tag="h5" className="fw-semibold mb-0">
            🔴 Indikator LED
          </CardText>
        </CardHeader>
        <CardBody className="pt-2">
          <div className="scanner-setting__led-grid">
            <div className="scanner-setting__led-item">
              <span className="scanner-setting__led-dot scanner-setting__led-dot--red" />
              <span className="small">Merah menyala: mengisi daya</span>
            </div>
            <div className="scanner-setting__led-item">
              <span className="scanner-setting__led-dot scanner-setting__led-dot--blue" />
              <span className="small">Biru tetap: terhubung dengan baik</span>
            </div>
            <div className="scanner-setting__led-item">
              <span className="scanner-setting__led-dot scanner-setting__led-dot--green" />
              <span className="small">Hijau berkedip: mode 2.4G</span>
            </div>
            <div className="scanner-setting__led-item">
              <span className="scanner-setting__led-dot scanner-setting__led-dot--blue-blink" />
              <span className="small">Biru berkedip: mode SPP</span>
            </div>
            <div className="scanner-setting__led-item">
              <span className="scanner-setting__led-dot scanner-setting__led-dot--green-blue" />
              <span className="small">
                Hijau &amp; Biru bergantian: mode BLE
              </span>
            </div>
          </div>
        </CardBody>
      </Card>

      {/* ─── Test Area ─── */}
      <Card className="border-0 shadow-sm mb-4" innerRef={testAreaRef}>
        <CardHeader className="bg-transparent border-bottom-0 pb-0 d-flex align-items-center justify-content-between">
          <div>
            <CardText tag="h5" className="fw-semibold mb-0">
              🧪 Uji Coba Scanner
            </CardText>
            <small className="text-muted">
              Pindai barcode apapun untuk menguji koneksi
            </small>
          </div>
          <div className="d-flex gap-2">
            <Button
              size="sm"
              color={isListening ? 'danger' : 'success'}
              outline
              data-testid="setting-scanner-listen-toggle-button"
              onClick={() => setIsListening(!isListening)}
            >
              {isListening ? '⏸ Pause' : '▶ Start'}
            </Button>
            {testScans.length > 0 && (
              <Button
                size="sm"
                color="secondary"
                outline
                data-testid="setting-scanner-clear-button"
                onClick={handleClearScans}
              >
                🗑 Hapus
              </Button>
            )}
          </div>
        </CardHeader>
        <CardBody>
          {/* Listening indicator */}
          <div
            className={`scanner-setting__listening ${isListening || isScanning ? 'scanner-setting__listening--active' : ''}`}
          >
            <div className="scanner-setting__listening-pulse" />
            <span>
              {isScanning
                ? '⏳ Memproses kode barcode (Loading)...'
                : isListening
                ? 'Mendengarkan... pindai barcode sekarang'
                : 'Scanner dijeda — klik Start untuk melanjutkan'}
            </span>
          </div>

          <div className="mt-4 p-3 border rounded text-center bg-light">
            <h6 className="fw-semibold mb-3">Generator Barcode Test Area</h6>
            <div className="d-flex justify-content-center mb-2">
              <Button
                color="warning"
                size="sm"
                data-testid="setting-scanner-focus-button"
                onClick={(e) => {
                  e.target.blur()
                  document.body.focus()
                }}
              >
                1. Klik Disini agar kursor menghilang sebelum Scan!
              </Button>
            </div>
            
            {/* Prominent Loading Overlay above Barcode */}
            {isScanning && (
              <div className="d-flex justify-content-center mb-3">
                <Alert color="warning" className="mb-0 d-flex align-items-center shadow-sm fw-bold border-warning py-2">
                  <Spinner size="sm" color="warning" className="me-2" />
                  ⏳ Memproses Barcode...
                </Alert>
              </div>
            )}

            <div className="d-flex align-items-center justify-content-center mb-3 gap-4 flex-wrap">
              <div className="bg-white p-3 rounded shadow-sm d-inline-block scanner-setting__barcode-container">
                <Barcode
                  value={generatedBarcode}
                  format="CODE128"
                  width={generatedBarcode.length > 50 ? 1 : 3}
                  height={120}
                  displayValue={generatedBarcode.length <= 50}
                  background="#ffffff"
                  margin={20}
                />
              </div>
              <div className="bg-white p-4 rounded shadow-sm d-inline-block">
                <QRCode value={generatedBarcode} size={180} level="H" />
              </div>
            </div>
            <small className="text-warning d-block mb-2">
              ⚠️ Jika scanner tidak bisa membaca barcode dari layar, coba
              perbesar zoom browser (Ctrl/Cmd + ), naikkan brightness layar,
              atau cetak barcode.
              <strong>
                {' '}
                Coba scan QR code — biasanya lebih mudah dibaca dari layar.
              </strong>
            </small>
            <div className="d-flex gap-2 mx-auto" style={{ maxWidth: '400px' }}>
              <Input
                type="text"
                data-testid="setting-scanner-barcode-input"
                placeholder="Masukkan nilai (contoh: 8991234567890)"
                value={barcodeInputValue}
                onChange={(e) => setBarcodeInputValue(e.target.value)}
                onKeyDown={(e) => {
                  if (e.key === 'Enter') {
                    setGeneratedBarcode(barcodeInputValue || '1234567890')
                    e.target.blur()
                  }
                }}
              />
              <Button
                color="primary"
                data-testid="setting-scanner-generate-button"
                onClick={() => {
                  setGeneratedBarcode(barcodeInputValue || '1234567890')
                  if (document.activeElement) document.activeElement.blur()
                }}
              >
                Generate
              </Button>
            </div>
            <small className="text-muted mt-2 d-block">
              Buat barcode di atas lalu scan dengan perangkat scanner Anda
            </small>
          </div>

          {/* Results */}
          {testScans.length === 0 ? (
            <div className="scanner-setting__empty-state">
              <span className="scanner-setting__empty-icon">📡</span>
              <p className="text-muted mb-0">
                Belum ada hasil scan. Arahkan scanner ke barcode dan tekan
                tombol scan.
              </p>
            </div>
          ) : (
            <div className="scanner-setting__results">
              {testScans.map((scan) => {
                const decodedJWT = decodeJWT(scan.value)
                return (
                  <div key={scan.id} className="scanner-setting__result-item flex-column align-items-start">
                    <div className="d-flex justify-content-between w-100 mb-1">
                      <span className="scanner-setting__result-value">
                        {scan.value}
                      </span>
                      <span className="scanner-setting__result-time">
                        {scan.timestamp}
                      </span>
                    </div>
                    {decodedJWT && (
                      <div className="mt-2 w-100 bg-white p-2 rounded border" style={{ fontSize: '13px' }}>
                        <strong className="text-primary d-block mb-1">JWT Payload (Decoded):</strong>
                        <pre className="mb-0 text-muted" style={{ whiteSpace: 'pre-wrap', wordBreak: 'break-all' }}>
                          {JSON.stringify(decodedJWT, null, 2)}
                        </pre>
                      </div>
                    )}
                  </div>
                )
              })}
            </div>
          )}
        </CardBody>
      </Card>
    </CardBody>
  )
}

export default ScannerSetting
